import {
  isProjectFileArchiveJunkPath,
  joinProjectFileUploadPath,
  PROJECT_FILE_UPLOAD_MAX_FILES,
  sanitizeProjectFileUploadPath,
  shouldSkipProjectFileFlattenMember,
} from '@shared/helpers/project-file-upload-normalize.helpers';

/**
 * One browser file plus the relative path to send as multer `originalname`.
 * `File.name` cannot contain slashes, so the path lives here — not on the File.
 */
export type ProjectFileClientUpload = {
  file: File;
  relativePath: string;
};

export type CollectProjectFilesOptions = {
  /** Keep facit sidecars, CAD/BIM names, and oversize files. */
  keepAllSourceFiles?: boolean;
};

const fileFromFileEntry = (entry: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });

const readAllDirectoryEntries = (
  dir: FileSystemDirectoryEntry,
): Promise<FileSystemEntry[]> =>
  new Promise((resolve, reject) => {
    const reader = dir.createReader();
    const all: FileSystemEntry[] = [];
    const pump = (): void => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        all.push(...batch);
        pump();
      }, reject);
    };
    pump();
  });

const toClientUpload = (
  file: File,
  relativePath: string,
  keepAllSourceFiles: boolean,
): ProjectFileClientUpload | null => {
  const path = sanitizeProjectFileUploadPath(relativePath);
  if (!path || isProjectFileArchiveJunkPath(path)) return null;
  if (file.size === 0) return null;
  if (
    !keepAllSourceFiles &&
    shouldSkipProjectFileFlattenMember(path, file.size)
  ) {
    return null;
  }
  return { file, relativePath: path };
};

/**
 * Map a file-picker {@link FileList} (including `webkitdirectory`) to uploads.
 * Uses `webkitRelativePath` when the browser fills it.
 */
export const collectProjectFilesFromFileList = (
  list: ArrayLike<File>,
  options?: CollectProjectFilesOptions,
): ProjectFileClientUpload[] => {
  const keepAllSourceFiles = options?.keepAllSourceFiles === true;
  const out: ProjectFileClientUpload[] = [];
  for (let i = 0; i < list.length; i++) {
    const file = list[i];
    if (!file) continue;
    const relative =
      file.webkitRelativePath && file.webkitRelativePath.trim() !== ''
        ? file.webkitRelativePath
        : file.name;
    const upload = toClientUpload(file, relative, keepAllSourceFiles);
    if (!upload) continue;
    if (out.length >= PROJECT_FILE_UPLOAD_MAX_FILES) break;
    out.push(upload);
  }
  return out;
};

const isFileSystemFileEntry = (
  entry: FileSystemEntry,
): entry is FileSystemFileEntry => entry.isFile;

const isFileSystemDirectoryEntry = (
  entry: FileSystemEntry,
): entry is FileSystemDirectoryEntry => entry.isDirectory;

const collectFromEntry = async (
  entry: FileSystemEntry,
  parentPath: string,
  keepAllSourceFiles: boolean,
): Promise<ProjectFileClientUpload[]> => {
  if (isFileSystemFileEntry(entry)) {
    const file = await fileFromFileEntry(entry);
    const upload = toClientUpload(
      file,
      joinProjectFileUploadPath(parentPath, entry.name),
      keepAllSourceFiles,
    );
    return upload ? [upload] : [];
  }
  if (!isFileSystemDirectoryEntry(entry)) return [];
  const dirPath = joinProjectFileUploadPath(parentPath, entry.name);
  if (!dirPath || isProjectFileArchiveJunkPath(dirPath)) return [];
  const children = await readAllDirectoryEntries(entry);
  const nested = await Promise.all(
    children.map((child) =>
      collectFromEntry(child, dirPath, keepAllSourceFiles),
    ),
  );
  return nested.flat();
};

const entryFromDataTransferItem = (
  item: DataTransferItem,
): FileSystemEntry | null => {
  if (typeof item.webkitGetAsEntry !== 'function') return null;
  return item.webkitGetAsEntry();
};

/**
 * Walk dropped folders via `webkitGetAsEntry`. Falls back to `files` when
 * the browser does not expose directory entries (plain file drop).
 */
export const collectProjectFilesFromDataTransfer = async (
  dataTransfer: DataTransfer,
  options?: CollectProjectFilesOptions,
): Promise<ProjectFileClientUpload[]> => {
  const keepAllSourceFiles = options?.keepAllSourceFiles === true;
  const entries = Array.from(dataTransfer.items)
    .filter((item) => item.kind === 'file')
    .map(entryFromDataTransferItem)
    .filter((entry): entry is FileSystemEntry => entry != null);

  if (entries.length === 0) {
    return collectProjectFilesFromFileList(dataTransfer.files, options);
  }

  const nested = await Promise.all(
    entries.map((entry) => collectFromEntry(entry, '', keepAllSourceFiles)),
  );
  return nested.flat().slice(0, PROJECT_FILE_UPLOAD_MAX_FILES);
};
